import { useEffect, useState } from "react";
import { DateField } from "@/components/DateField";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError } from "@/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LAST = "airthermo:last-ticket-project";

export function NewTicketDialog({ lead, open, onClose, onCreated }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [lists, setLists] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ list_id: "", title: "", notes: "", assignee_id: "", due_date: "" });
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    set({ title: "", notes: "", assignee_id: "", due_date: "" });
    api.get("/projects").then(({ data }) => {
      setProjects(data);
      const last = localStorage.getItem(LAST);
      setProjectId(data.some((p) => p.id === last) ? last : data[0]?.id || "");
    }).catch(() => setProjects([]));
  }, [open]);

  useEffect(() => {
    if (!projectId) { setLists([]); setMembers([]); return undefined; }
    let alive = true;
    setLists([]); set({ list_id: "" });
    Promise.all([api.get(`/projects/${projectId}/todos`), api.get(`/projects/${projectId}`)]).then(([t, p]) => {
      if (!alive) return;
      setLists(t.data.lists); setMembers(p.data.members_info || []);
      set({ list_id: t.data.lists[0]?.id || "", assignee_id: "" });
    }).catch(() => { if (alive) { setLists([]); setMembers([]); } });
    return () => { alive = false; };
  }, [projectId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/todos`, { ...form, assignee_id: form.assignee_id || null, due_date: form.due_date || null, lead_ids: [lead.lead_id] });
      localStorage.setItem(LAST, projectId);
      toast.success("Ticket créé et lié au dossier.");
      onCreated(); onClose();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="pl-ticket-dialog" data-testid="new-ticket-dialog">
        <DialogHeader><DialogTitle>Nouveau ticket · {lead.title}</DialogTitle></DialogHeader>
        {projects.length === 0 ? <p className="pl-muted" data-testid="new-ticket-noproject">Aucun projet disponible : créez d'abord un projet dans Projets.</p> : (
          <form onSubmit={submit} className="pl-ticket-form">
            <div className="pl-ticket-row">
              <label>Projet<select className="pl-select" value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="new-ticket-project">{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
              <label>Liste<select className="pl-select" value={form.list_id} onChange={(e) => set({ list_id: e.target.value })} required data-testid="new-ticket-list">{lists.length === 0 && <option value="">Aucune liste dans ce projet</option>}{lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></label>
            </div>
            <label>Titre<input className="pl-select" value={form.title} onChange={(e) => set({ title: e.target.value })} required maxLength={300} autoFocus placeholder="Ex. Relancer la mairie pour le devis" data-testid="new-ticket-title" /></label>
            <label>Description<textarea className="pl-select" rows={4} value={form.notes} onChange={(e) => set({ notes: e.target.value })} maxLength={5000} data-testid="new-ticket-notes" /></label>
            <div className="pl-ticket-row">
              <label>Assigné à<select className="pl-select" value={form.assignee_id} onChange={(e) => set({ assignee_id: e.target.value })} data-testid="new-ticket-assignee"><option value="">Personne</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></label>
              <label>Échéance<DateField value={form.due_date} onChange={(v) => set({ due_date: v })} placeholder="Aucune" testid="new-ticket-due" /></label>
            </div>
            <div className="pl-ticket-foot">
              <button type="button" className="secondary-button" onClick={onClose}>Annuler</button>
              <button type="submit" className="primary-button" disabled={busy || !form.list_id} data-testid="new-ticket-submit">{busy ? "Création…" : "Créer le ticket"}</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
